import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Badge, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import { useAuthStore } from '@/stores/auth';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './Templates.module.css';

/* ──────── Types ──────── */

interface Variable { key: string; label: string; type: string }

interface TemplateSection {
  id: string;
  type: 'heading' | 'meta' | 'article' | 'notice' | 'checklist' | 'signature' | 'footer' | 'custom';
  title?: string;
  number?: number;
  content: string;
  locked?: boolean;
}

interface Template {
  id: string;
  template_type: string;
  name: string;
  description: string;
  html_content?: string;
  css?: string | null;
  variables: Variable[];
  sections?: TemplateSection[];
  current_version?: number;
  is_default: boolean;
  is_master?: boolean;
  published_at?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface VersionEntry {
  version: number;
  summary: string;
  created_at: string;
}

interface ImportPreview {
  source_format: string;
  source_filename: string;
  detected_sections: TemplateSection[];
  html_preview: string;
  detected_variables: string[];
  warnings: string[];
}

/* ──────── Constants ──────── */

const typeLabel: Record<string, string> = {
  lease: '賃貸借契約書',
  important_explanation: '重要事項説明書',
  sales: '売買契約書',
  management: '管理委託契約書',
};
const typeVariant: Record<string, 'blue' | 'green' | 'amber' | 'purple'> = {
  lease: 'blue', important_explanation: 'amber', sales: 'green', management: 'purple',
};

const sectionTypeLabel: Record<TemplateSection['type'], string> = {
  heading: '表題',
  meta: 'メタ情報',
  article: '条文',
  notice: '注意事項',
  checklist: 'チェックリスト',
  signature: '署名欄',
  footer: 'フッター',
  custom: 'カスタム',
};

const sectionTypeIcon: Record<TemplateSection['type'], string> = {
  heading: 'H',
  meta: 'i',
  article: '#',
  notice: '!',
  checklist: '☑',
  signature: '✍',
  footer: '_',
  custom: '*',
};

const AVAILABLE_VARIABLES = [
  { key: 'property_name', label: '物件名' },
  { key: 'property_address', label: '物件住所' },
  { key: 'landlord_name', label: '貸主名' },
  { key: 'tenant_name', label: '借主名' },
  { key: 'rent_amount', label: '賃料' },
  { key: 'deposit_amount', label: '敷金' },
  { key: 'key_money', label: '礼金' },
  { key: 'contract_start', label: '契約開始日' },
  { key: 'contract_end', label: '契約終了日' },
  { key: 'contract_date', label: '契約日' },
  { key: 'management_fee', label: '管理費' },
  { key: 'agent_name', label: '仲介業者名' },
  { key: 'agent_license', label: '免許番号' },
];

/* ──────── Helpers ──────── */

function genId(): string {
  return 'sec_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

function sectionsToHtml(sections: TemplateSection[]): string {
  return sections.map(sec => {
    switch (sec.type) {
      case 'heading': return `<h1>${sec.content}</h1>`;
      case 'meta': return `<div class="meta">${sec.content}</div>`;
      case 'article': {
        const heading = sec.number
          ? `<h2>第${sec.number}条 ${sec.title || ''}</h2>`
          : `<h2>${sec.title || ''}</h2>`;
        return `${heading}\n<div class="section">\n${sec.content}\n</div>`;
      }
      case 'notice': return `<div class="section" style="background:#fffbeb;border-color:#f59e0b">\n${sec.content}\n</div>`;
      case 'checklist': return `<div class="section">\n${sec.content}\n</div>`;
      case 'signature': return `<div class="sig-area">\n${sec.content}\n</div>`;
      case 'footer': return `<div class="footer">${sec.content}</div>`;
      case 'custom': return `<div class="section">\n${sec.content}\n</div>`;
      default: return sec.content;
    }
  }).join('\n');
}

function extractVariables(sections: TemplateSection[]): string[] {
  const all = sections.map(s => s.content + (s.title || '')).join(' ');
  const matches = all.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
}

function buildPreviewHtml(sections: TemplateSection[], css?: string | null): string {
  const body = sectionsToHtml(sections);
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
body{font-family:"Noto Sans JP","Hiragino Kaku Gothic ProN",sans-serif;padding:40px;max-width:800px;margin:0 auto;font-size:14px;line-height:1.8;color:#1a1a1a}
h1{text-align:center;font-size:22px;margin-bottom:24px;border-bottom:2px solid #333;padding-bottom:12px}
h2{font-size:16px;margin:20px 0 8px;font-weight:700}
.meta{background:#f9fafb;padding:16px;border:1px solid #e5e7eb;border-radius:4px;margin:12px 0;font-size:13px}
.section{margin:8px 0 16px;padding:12px 16px;border-left:3px solid #d1d5db}
.sig-area{margin:40px 0 16px;padding:24px;border:1px dashed #9ca3af;text-align:center}
.footer{margin-top:40px;padding-top:16px;border-top:1px solid #d1d5db;font-size:12px;color:#6b7280;text-align:center}
table{width:100%;border-collapse:collapse;margin:12px 0}
th,td{border:1px solid #d1d5db;padding:8px 12px;text-align:left;font-size:13px}
th{background:#f3f4f6;font-weight:600}
${css || ''}
</style></head><body>${body}</body></html>`;
}

const fmtDate = (d: string) => new Date(d).toLocaleDateString('ja-JP');

/* ──────── Sortable Section Item ──────── */

interface SortableSectionProps {
  section: TemplateSection;
  isSelected: boolean;
  isMaster?: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onToggleLock?: () => void;
}

function SortableSectionItem({ section, isSelected, isMaster, onSelect, onDelete, onToggleLock }: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.sectionItem} ${isSelected ? styles.sectionItemActive : ''}`}
      onClick={onSelect}
    >
      <span className={styles.dragHandle} {...attributes} {...listeners}>☰</span>
      <span className={styles.sectionIcon}>{sectionTypeIcon[section.type]}</span>
      <div className={styles.sectionInfo}>
        <span className={styles.sectionType}>{sectionTypeLabel[section.type]}</span>
        {section.title && <span className={styles.sectionTitle}>{section.title}</span>}
      </div>
      {isMaster && (
        <button
          className={styles.sectionLockBtn}
          onClick={e => { e.stopPropagation(); onToggleLock?.(); }}
          title={section.locked ? 'ロック解除' : 'ロック'}
        >
          {section.locked ? '\uD83D\uDD12' : '\uD83D\uDD13'}
        </button>
      )}
      <button className={styles.sectionDeleteBtn} onClick={e => { e.stopPropagation(); onDelete(); }}>✕</button>
    </div>
  );
}

/* ──────── TipTap Toolbar ──────── */

interface ToolbarProps {
  editor: ReturnType<typeof useEditor>;
  onInsertVariable: (key: string) => void;
}

function EditorToolbar({ editor, onInsertVariable }: ToolbarProps) {
  const [showVars, setShowVars] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowVars(false);
      }
    };
    if (showVars) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showVars]);

  if (!editor) return null;

  const btn = (label: string, action: () => void, isActive?: boolean) => (
    <button
      type="button"
      className={`${styles.toolbarBtn} ${isActive ? styles.toolbarBtnActive : ''}`}
      onClick={action}
      title={label}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.toolbar2}>
      {btn('B', () => editor.chain().focus().toggleBold().run(), editor.isActive('bold'))}
      {btn('I', () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'))}
      {btn('U', () => editor.chain().focus().toggleUnderline().run(), editor.isActive('underline'))}
      {btn('S', () => editor.chain().focus().toggleStrike().run(), editor.isActive('strike'))}
      <span className={styles.toolbarDivider} />
      {btn('H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 }))}
      {btn('H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 }))}
      <span className={styles.toolbarDivider} />
      {btn('•', () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'))}
      {btn('1.', () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'))}
      <span className={styles.toolbarDivider} />
      {btn('⇤', () => editor.chain().focus().setTextAlign('left').run(), editor.isActive({ textAlign: 'left' }))}
      {btn('⇆', () => editor.chain().focus().setTextAlign('center').run(), editor.isActive({ textAlign: 'center' }))}
      {btn('⇥', () => editor.chain().focus().setTextAlign('right').run(), editor.isActive({ textAlign: 'right' }))}
      <span className={styles.toolbarDivider} />
      <div className={styles.varDropdownWrap} ref={dropdownRef}>
        <button type="button" className={styles.varInsertBtn} onClick={() => setShowVars(!showVars)}>
          {'{{ }}'} 変数
        </button>
        {showVars && (
          <div className={styles.varDropdown}>
            {AVAILABLE_VARIABLES.map(v => (
              <button
                key={v.key}
                type="button"
                className={styles.varDropdownItem}
                onClick={() => { onInsertVariable(v.key); setShowVars(false); }}
              >
                <code>{`{{${v.key}}}`}</code>
                <span>{v.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────── Main Component ──────── */

export function Templates() {
  const { isAdmin, isSuperAdmin } = useRole();
  const { data: templates, refetch } = useApi<Template[]>('/console/templates');

  // List state
  const [filter, setFilter] = useState<string | null>(null);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selected, setSelected] = useState<Template | null>(null);
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formType, setFormType] = useState('lease');
  const [formCss, setFormCss] = useState('');
  const [formDefault, setFormDefault] = useState(false);
  const [variables, setVariables] = useState<Variable[]>([]);
  const [activeTab, setActiveTab] = useState<'edit' | 'history' | 'variables'>('edit');

  // Version history
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Create modal / wizard
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ template_type: 'lease', name: '', description: '' });
  const [createStep, setCreateStep] = useState<'method' | 'type' | 'info'>('method');
  const [createMethod, setCreateMethod] = useState<'empty' | 'template' | 'import'>('template');
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reimportFileInputRef = useRef<HTMLInputElement>(null);
  const [showReimport, setShowReimport] = useState(false);

  const filtered = templates?.filter(t => !filter || t.template_type === filter) ?? [];
  const activeSection = sections.find(s => s.id === activeSectionId) ?? null;

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'セクションの内容を入力...' }),
    ],
    content: '',
    onUpdate: ({ editor: ed }) => {
      if (!activeSectionId) return;
      const html = ed.getHTML();
      setSections(prev => prev.map(s => s.id === activeSectionId ? { ...s, content: html } : s));
    },
  });

  // Sync editor content when active section changes
  useEffect(() => {
    if (!editor) return;
    if (activeSection) {
      const currentContent = editor.getHTML();
      if (currentContent !== activeSection.content) {
        editor.commands.setContent(activeSection.content || '');
      }
    } else {
      editor.commands.setContent('');
    }
  }, [activeSectionId, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detected variables
  const detectedVars = useMemo(() => extractVariables(sections), [sections]);

  // Preview HTML
  const previewHtml = useMemo(() => buildPreviewHtml(sections, formCss), [sections, formCss]);

  /* ── Actions ── */

  const openCreate = () => {
    setCreateForm({ template_type: 'lease', name: '', description: '' });
    setCreateStep('method');
    setCreateMethod('template');
    setImportPreview(null);
    setShowCreate(true);
  };

  const handleCreate = async () => {
    try {
      const importSections = importPreview?.detected_sections;
      const defaultSections: TemplateSection[] = (createMethod === 'import' && importSections && importSections.length > 0)
        ? importSections.map(s => ({ ...s, id: s.id || genId() }))
        : [
          { id: genId(), type: 'heading', content: '<p>' + createForm.name + '</p>' },
          { id: genId(), type: 'meta', content: '<p>契約情報をここに記載</p>' },
          { id: genId(), type: 'article', title: '目的', number: 1, content: '<p>条文の内容</p>' },
        ];
      await api('/console/templates', {
        method: 'POST',
        body: JSON.stringify({
          template_type: createForm.template_type,
          name: createForm.name,
          description: createForm.description,
          sections: defaultSections,
          html_content: sectionsToHtml(defaultSections),
          variables: [],
          is_default: false,
        }),
      });
      toast('テンプレートを作成しました');
      setShowCreate(false);
      refetch();
    } catch { toast('作成に失敗しました', 'error'); }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast('ファイルサイズは10MB以下にしてください', 'error'); return; }
    setSelectedFile(file);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/templates/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || 'ファイルの変換に失敗しました', 'error'); setSelectedFile(null); return; }
      setImportPreview(data);
      if (data.detected_sections) {
        setCreateForm(f => ({ ...f, name: file.name.replace(/\.\w+$/, '') }));
      }
    } catch { toast('ファイルの変換に失敗しました', 'error'); setSelectedFile(null); }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setImportPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleReimport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast('ファイルサイズは10MB以下にしてください', 'error'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/templates/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || 'ファイルの変換に失敗しました', 'error'); return; }
      if (data.detected_sections) {
        const newSections = data.detected_sections.map((s: TemplateSection) => ({ ...s, id: s.id || genId() }));
        setSections(newSections);
        setActiveSectionId(newSections[0]?.id ?? null);
        toast(`${data.detected_sections.length}個のセクションをインポートしました`);
      }
      setShowReimport(false);
    } catch { toast('ファイルの変換に失敗しました', 'error'); }
  };

  const handlePublish = async () => {
    if (!selected) return;
    try {
      await api(`/console/templates/${selected.id}/publish`, { method: 'POST' });
      toast('公開しました');
      refetch();
    } catch { toast('公開に失敗しました', 'error'); }
  };

  const toggleSectionLock = (sectionId: string) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, locked: !s.locked } : s
    ));
  };

  const openEditor = async (tpl: Template) => {
    try {
      const detail = await api<Template>(`/console/templates/${tpl.id}`);
      setSelected(detail);
      setFormName(detail.name);
      setFormDesc(detail.description || '');
      setFormType(detail.template_type);
      setFormCss(detail.css || '');
      setFormDefault(detail.is_default);
      setVariables(detail.variables || []);

      // Load sections or generate from html_content
      const secs = detail.sections && detail.sections.length > 0
        ? detail.sections
        : [{ id: genId(), type: 'custom' as const, content: detail.html_content || '' }];
      setSections(secs);
      setActiveSectionId(secs[0]?.id ?? null);
      setActiveTab('edit');
      setEditorOpen(true);
    } catch { toast('取得に失敗しました', 'error'); }
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      await api(`/console/templates/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: formName,
          description: formDesc,
          sections,
          html_content: sectionsToHtml(sections),
          css: formCss || null,
          variables,
          is_default: formDefault,
        }),
      });
      toast('テンプレートを更新しました');
      refetch();
    } catch { toast('更新に失敗しました', 'error'); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('このテンプレートを削除しますか？')) return;
    try {
      await api(`/console/templates/${id}`, { method: 'DELETE' });
      toast('削除しました');
      refetch();
    } catch { toast('削除に失敗しました', 'error'); }
  };

  // Sections manipulation
  const addSection = (type: TemplateSection['type']) => {
    const sec: TemplateSection = {
      id: genId(),
      type,
      title: type === 'article' ? '' : undefined,
      number: type === 'article' ? (sections.filter(s => s.type === 'article').length + 1) : undefined,
      content: '',
    };
    setSections(prev => [...prev, sec]);
    setActiveSectionId(sec.id);
  };

  const removeSection = (id: string) => {
    setSections(prev => {
      const next = prev.filter(s => s.id !== id);
      if (activeSectionId === id) {
        setActiveSectionId(next[0]?.id ?? null);
      }
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections(prev => {
      const oldIndex = prev.findIndex(s => s.id === active.id);
      const newIndex = prev.findIndex(s => s.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const updateSectionMeta = (field: 'title' | 'number', value: string | number) => {
    if (!activeSectionId) return;
    setSections(prev => prev.map(s =>
      s.id === activeSectionId ? { ...s, [field]: value } : s
    ));
  };

  const insertVariable = useCallback((key: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${key}}}`).run();
  }, [editor]);

  // Version history
  const loadVersions = async () => {
    if (!selected) return;
    setVersionsLoading(true);
    try {
      const data = await api<VersionEntry[]>(`/console/templates/${selected.id}/versions`);
      setVersions(data);
    } catch {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleRollback = async (version: number) => {
    if (!selected) return;
    if (!confirm(`バージョン ${version} にロールバックしますか？`)) return;
    try {
      await api(`/console/templates/${selected.id}/rollback/${version}`, { method: 'POST' });
      toast(`バージョン ${version} にロールバックしました`);
      // Reload template
      const detail = await api<Template>(`/console/templates/${selected.id}`);
      setSelected(detail);
      const secs = detail.sections && detail.sections.length > 0
        ? detail.sections
        : [{ id: genId(), type: 'custom' as const, content: detail.html_content || '' }];
      setSections(secs);
      setActiveSectionId(secs[0]?.id ?? null);
      setVariables(detail.variables || []);
      loadVersions();
      refetch();
    } catch { toast('ロールバックに失敗しました', 'error'); }
  };

  // Variable management
  const updateVariableLabel = (key: string, label: string) => {
    setVariables(prev => {
      const existing = prev.find(v => v.key === key);
      if (existing) return prev.map(v => v.key === key ? { ...v, label } : v);
      return [...prev, { key, label, type: 'text' }];
    });
  };

  const updateVariableType = (key: string, type: string) => {
    setVariables(prev => {
      const existing = prev.find(v => v.key === key);
      if (existing) return prev.map(v => v.key === key ? { ...v, type } : v);
      return [...prev, { key, label: key, type }];
    });
  };

  // Switch tabs and load data
  const switchTab = (tab: 'edit' | 'history' | 'variables') => {
    setActiveTab(tab);
    if (tab === 'history') loadVersions();
  };

  /* ── Render ── */

  // Full-screen editor
  if (editorOpen && selected) {
    return (
      <div className={styles.editorFullscreen}>
        {/* Header */}
        <div className={styles.editorHeader}>
          <button className={styles.backBtn} onClick={() => setEditorOpen(false)}>&larr; 戻る</button>
          <div className={styles.editorHeaderMeta}>
            <input
              className={styles.editorTitleInput}
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="テンプレート名"
            />
            <input
              className={styles.editorDescInput}
              value={formDesc}
              onChange={e => setFormDesc(e.target.value)}
              placeholder="説明"
            />
          </div>
          <label className={styles.defaultCheck}>
            <input type="checkbox" checked={formDefault} onChange={e => setFormDefault(e.target.checked)} />
            デフォルト
          </label>
          <button className={styles.actionBtn} onClick={() => setShowReimport(true)}>📂 再インポート</button>
          {selected?.is_master && (
            <button className={styles.publishBtn} onClick={handlePublish}>公開</button>
          )}
          <button className={styles.saveBtn} onClick={handleSave}>保存</button>
        </div>

        {/* Tabs */}
        <div className={styles.editorTabs}>
          <button className={`${styles.editorTab} ${activeTab === 'edit' ? styles.editorTabActive : ''}`} onClick={() => switchTab('edit')}>編集</button>
          <button className={`${styles.editorTab} ${activeTab === 'history' ? styles.editorTabActive : ''}`} onClick={() => switchTab('history')}>履歴</button>
          <button className={`${styles.editorTab} ${activeTab === 'variables' ? styles.editorTabActive : ''}`} onClick={() => switchTab('variables')}>変数</button>
        </div>

        {activeTab === 'edit' && (
          <div className={styles.editorBody}>
            {/* Left: Section list */}
            <div className={styles.sectionPanel}>
              <div className={styles.sectionPanelHeader}>セクション</div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className={styles.sectionList}>
                    {sections.map(sec => (
                      <SortableSectionItem
                        key={sec.id}
                        section={sec}
                        isSelected={sec.id === activeSectionId}
                        isMaster={selected?.is_master}
                        onSelect={() => setActiveSectionId(sec.id)}
                        onDelete={() => removeSection(sec.id)}
                        onToggleLock={() => toggleSectionLock(sec.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <div className={styles.addSectionWrap}>
                <select
                  className={styles.addSectionSelect}
                  defaultValue=""
                  onChange={e => { if (e.target.value) { addSection(e.target.value as TemplateSection['type']); e.target.value = ''; } }}
                >
                  <option value="" disabled>+ セクション追加</option>
                  {(Object.keys(sectionTypeLabel) as TemplateSection['type'][]).map(t => (
                    <option key={t} value={t}>{sectionTypeLabel[t]}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Center: Editor */}
            <div className={styles.editorCenter}>
              {activeSection ? (
                <>
                  {activeSection.type === 'article' && (
                    <div className={styles.sectionMetaRow}>
                      <div className={styles.metaField}>
                        <label>条文番号</label>
                        <input
                          type="number"
                          value={activeSection.number || ''}
                          onChange={e => updateSectionMeta('number', parseInt(e.target.value) || 0)}
                          className={styles.metaInput}
                        />
                      </div>
                      <div className={styles.metaField}>
                        <label>タイトル</label>
                        <input
                          value={activeSection.title || ''}
                          onChange={e => updateSectionMeta('title', e.target.value)}
                          className={styles.metaInput}
                          placeholder="例: 目的"
                        />
                      </div>
                    </div>
                  )}
                  <EditorToolbar editor={editor} onInsertVariable={insertVariable} />
                  <div className={styles.tiptapWrap}>
                    <EditorContent editor={editor} className={styles.tiptapEditor} />
                  </div>
                </>
              ) : (
                <div className={styles.noSection}>セクションを選択してください</div>
              )}
            </div>

            {/* Right: Preview */}
            <div className={styles.previewPanel}>
              <div className={styles.previewPanelHeader}>プレビュー</div>
              <iframe
                className={styles.previewIframe}
                srcDoc={previewHtml}
                title="preview"
                sandbox="allow-same-origin allow-scripts allow-modals"
              />
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className={styles.historyPanel}>
            <Card title={`バージョン履歴 (現在: v${selected.current_version || 1})`}>
              {versionsLoading ? (
                <div className={styles.empty}>読み込み中...</div>
              ) : versions.length === 0 ? (
                <div className={styles.empty}>履歴がありません</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>バージョン</th>
                      <th>変更概要</th>
                      <th>日時</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map(v => (
                      <tr key={v.version}>
                        <td><Badge variant="blue">v{v.version}</Badge></td>
                        <td>{v.summary || '—'}</td>
                        <td style={{ fontSize: '12px', color: 'var(--ink3)' }}>{fmtDate(v.created_at)}</td>
                        <td>
                          {v.version !== selected.current_version && (
                            <button className={styles.actionBtn} onClick={() => handleRollback(v.version)}>
                              ロールバック
                            </button>
                          )}
                          {v.version === selected.current_version && (
                            <Badge variant="green">現在</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'variables' && (
          <div className={styles.variablesPanel}>
            <Card title="変数管理">
              <p className={styles.variablesHint}>
                セクション内の <code>{'{{variable}}'}</code> が自動検出されます。各変数にラベルと型を設定できます。
              </p>
              {detectedVars.length === 0 ? (
                <div className={styles.empty}>変数が検出されていません</div>
              ) : (
                <table className={styles.varsTable}>
                  <thead>
                    <tr>
                      <th>キー</th>
                      <th>ラベル</th>
                      <th>型</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detectedVars.map(key => {
                      const v = variables.find(vv => vv.key === key);
                      return (
                        <tr key={key}>
                          <td><code>{`{{${key}}}`}</code></td>
                          <td>
                            <input
                              className={styles.varInput}
                              value={v?.label || ''}
                              onChange={e => updateVariableLabel(key, e.target.value)}
                              placeholder="ラベル"
                            />
                          </td>
                          <td>
                            <select
                              className={styles.varSelect}
                              value={v?.type || 'text'}
                              onChange={e => updateVariableType(key, e.target.value)}
                            >
                              <option value="text">テキスト</option>
                              <option value="number">数値</option>
                              <option value="date">日付</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        )}
      </div>
    );
  }

  /* ── List View ── */
  return (
    <div className={styles.container}>
      <Card title="契約テンプレート管理">
        <div className={styles.toolbar}>
          <button className={`${styles.filterBtn} ${!filter ? styles.filterBtnActive : ''}`} onClick={() => setFilter(null)}>全て</button>
          {Object.entries(typeLabel).map(([k, v]) => (
            <button key={k} className={`${styles.filterBtn} ${filter === k ? styles.filterBtnActive : ''}`} onClick={() => setFilter(k)}>{v}</button>
          ))}
          {isAdmin && <button className={styles.addBtn} onClick={openCreate}>+ 新規作成</button>}
        </div>

        {filtered.length === 0 ? (
          <div className={styles.empty}>テンプレートがありません</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>種別</th>
                <th>テンプレート名</th>
                <th>説明</th>
                <th>マスター</th>
                <th>公開</th>
                <th>デフォルト</th>
                <th>ステータス</th>
                <th>更新日</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(tpl => (
                <tr key={tpl.id}>
                  <td><Badge variant={typeVariant[tpl.template_type] || 'gray'}>{typeLabel[tpl.template_type] || tpl.template_type}</Badge></td>
                  <td style={{ fontWeight: 600 }}>{tpl.name}</td>
                  <td style={{ color: 'var(--ink3)', fontSize: '12px' }}>{tpl.description}</td>
                  <td>{tpl.is_master ? <Badge variant="blue">マスター</Badge> : null}</td>
                  <td>{tpl.published_at ? fmtDate(tpl.published_at) : '—'}</td>
                  <td>{tpl.is_default ? <Badge variant="green">デフォルト</Badge> : '—'}</td>
                  <td><Badge variant={tpl.status === 'active' ? 'green' : 'gray'}>{tpl.status === 'active' ? '有効' : '無効'}</Badge></td>
                  <td style={{ fontSize: '12px', color: 'var(--ink3)' }}>{fmtDate(tpl.updated_at)}</td>
                  <td>
                    <div className={styles.actions}>
                      {isAdmin && <button className={styles.actionBtn} onClick={() => openEditor(tpl)}>編集</button>}
                      {isSuperAdmin && <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(tpl.id)}>削除</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Create wizard modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="テンプレート新規作成" width={600} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {createStep !== 'method' && (
            <button className={styles.actionBtn} onClick={() => {
              if (createStep === 'info') setCreateStep(createMethod === 'empty' ? 'method' : 'type');
              else setCreateStep('method');
            }}>戻る</button>
          )}
          {createStep === 'info' && (
            <button className={styles.addBtn} onClick={handleCreate}>作成</button>
          )}
        </div>
      }>
        {/* Step 1: method */}
        {createStep === 'method' && (
          <div className={styles.wizardCards}>
            <div className={styles.wizardCard} onClick={() => { setCreateMethod('empty'); setCreateStep('info'); }}>
              <span className={styles.wizardCardIcon}>📝</span>
              <span className={styles.wizardCardLabel}>空から作成</span>
            </div>
            <div className={styles.wizardCard} onClick={() => { setCreateMethod('template'); setCreateStep('type'); }}>
              <span className={styles.wizardCardIcon}>📋</span>
              <span className={styles.wizardCardLabel}>雛形から作成</span>
            </div>
            <div className={styles.wizardCard} onClick={() => { setCreateMethod('import'); setCreateStep('type'); }}>
              <span className={styles.wizardCardIcon}>📂</span>
              <span className={styles.wizardCardLabel}>ファイルから作成</span>
            </div>
          </div>
        )}

        {/* Step 2: type */}
        {createStep === 'type' && (
          <div className={styles.wizardCards}>
            {Object.entries(typeLabel).map(([k, v]) => (
              <div key={k} className={styles.wizardCard} onClick={() => { setCreateForm(f => ({ ...f, template_type: k })); setCreateStep('info'); }}>
                <span className={styles.wizardCardIcon}>{k === 'lease' ? '📄' : k === 'important_explanation' ? '⚖️' : k === 'sales' ? '🤝' : '🔑'}</span>
                <span className={styles.wizardCardLabel}>{v}</span>
              </div>
            ))}
          </div>
        )}

        {/* Step 3: info */}
        {createStep === 'info' && (
          <>
            <div className={styles.formGroup}>
              <label>テンプレート名</label>
              <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="例: 賃貸借契約書（標準）" />
            </div>
            <div className={styles.formGroup}>
              <label>説明</label>
              <input value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder="テンプレートの説明" />
            </div>
            {createMethod === 'import' && (
              <div className={styles.importArea}>
                <input ref={fileInputRef} className={styles.fileInputHidden} type="file" accept=".xlsx,.xls,.docx,.pdf" onChange={handleFileSelect} />
                {!selectedFile ? (
                  <>
                    <button type="button" className={styles.fileSelectBtn} onClick={() => fileInputRef.current?.click()}>
                      📂 ファイルを選択
                    </button>
                    <p className={styles.importHint}>対応形式: .xlsx .xls .docx .pdf（最大10MB）</p>
                  </>
                ) : (
                  <>
                    <div className={styles.fileInfo}>
                      <span className={styles.fileIcon}>📄</span>
                      <div className={styles.fileDetails}>
                        <div className={styles.fileName}>{selectedFile.name}</div>
                        <div className={styles.fileSize}>{formatFileSize(selectedFile.size)}</div>
                      </div>
                      <button type="button" className={styles.fileClear} onClick={clearSelectedFile} title="ファイルを解除">✕</button>
                    </div>
                    {importPreview && (
                      <div className={styles.importPreview}>
                        <span>検出セクション: {importPreview.detected_sections.length}個</span>
                        <span>形式: {importPreview.source_format}</span>
                        {importPreview.warnings.length > 0 && (
                          <div className={styles.importWarnings}>
                            {importPreview.warnings.map((w, i) => <p key={i} style={{ color: '#b45c08', fontSize: 12, margin: '4px 0' }}>{w}</p>)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Reimport modal */}
      <Modal open={showReimport} onClose={() => setShowReimport(false)} title="ファイルから再インポート" width={500}>
        <div className={styles.importArea}>
          <input ref={reimportFileInputRef} className={styles.fileInputHidden} type="file" accept=".xlsx,.xls,.docx,.pdf" onChange={handleReimport} />
          <button type="button" className={styles.fileSelectBtn} onClick={() => reimportFileInputRef.current?.click()}>
            📂 ファイルを選択
          </button>
          <p className={styles.importHint}>対応形式: .xlsx .xls .docx .pdf（最大10MB）</p>
          <p style={{ color: 'var(--amber)', fontSize: 12, marginTop: 8 }}>既存のセクションは上書きされます。</p>
        </div>
      </Modal>
    </div>
  );
}
